import { friendlyError } from '../../../utils/userMessages';
import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert, Autocomplete, Avatar, Box, Button, Chip, CircularProgress, Container,
  Grid, Paper, Stack, TextField, Typography, alpha, MenuItem
} from '@mui/material';
import {
  AutoAwesomeRounded, Block as BlockRounded, BusinessRounded, Public as PublicRounded,
  Refresh as RefreshRounded, Save as SaveRounded, Security as SecurityRounded, CalendarMonth as CalendarMonthRounded,
  EventRounded as EventAvailableRounded, Link as LinkRounded, Delete as DeleteOutlineRounded, NotificationsActive as NotificationsActiveRounded, ScheduleRounded as AccessTimeRounded, PhotoCamera as PhotoCameraRounded
} from '@mui/icons-material';
import { collection, doc, getDocs, serverTimestamp, updateDoc } from 'firebase/firestore';
import toast from 'react-hot-toast';
import { useAuth } from '../AuthContext';
import { db } from '../../../config/firebase';
import Header from '../../common/Header/Header';
import { brandPalette as palette } from '../../../utils/brandPalette';
import { generateIntroduction, regenerateMyMatches } from '../../../services/integrations/ai';
import { resolveCompanyId } from '../../../utils/companyIdentity';
import { registerPushToken } from '../../../services/integrations/push';
import { uploadFile } from '../../../services/integrations/storage';

const cardSx = (accent = 'var(--primary)') => ({
  position: 'relative', overflow: 'hidden', borderRadius: '16px', p: { xs: 2, md: 2.3 },
  border: `1px solid ${alpha(accent, 0.12)}`,
  background: `radial-gradient(circle at 96% 4%, ${alpha(accent, 0.10)}, transparent 28%), rgba(255,255,255,.94)`,
  boxShadow: '0 12px 34px rgba(25,57,78,.085), inset 0 1px 0 rgba(255,255,255,.96)',
});

export default function ProfileSettings() {
  const { currentUser, userData, companyData } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [blockedCountries, setBlockedCountries] = useState([]);
  const [blockedCompanies, setBlockedCompanies] = useState([]);
  const [countryInput, setCountryInput] = useState('');
  const [aiIntroDraft, setAiIntroDraft] = useState('');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [workingHours, setWorkingHours] = useState({
    start: '09:00',
    end: '17:00',
    days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
    slotMinutes: 30,
    defaultMeetingDuration: 30,
  });
  const companyId = resolveCompanyId({ companyData, userData, currentUser });

  useEffect(() => {
    let active = true;
    const load = async () => {
      if (!currentUser?.uid) { setLoading(false); return; }
      try {
        const privacy = userData?.privacy || {};
        if (active) {
          setBlockedCountries(Array.isArray(privacy.blockedCountries) ? privacy.blockedCountries : Array.isArray(userData?.blockedCountries) ? userData.blockedCountries : []);
          setBlockedCompanies(Array.isArray(privacy.blockedCompanies) ? privacy.blockedCompanies : Array.isArray(userData?.blockedCompanies) ? userData.blockedCompanies : []);
          setAiIntroDraft(String(userData?.aiIntroduction || ''));
          const hours = userData?.workingHours || {};
          setWorkingHours({
            start: String(hours.start || '09:00'),
            end: String(hours.end || '17:00'),
            days: Array.isArray(hours.days) && hours.days.length ? hours.days.map((d) => String(d).toLowerCase()) : ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'],
            slotMinutes: Number(hours.slotMinutes || 30),
            defaultMeetingDuration: Number(hours.defaultMeetingDuration || userData?.defaultMeetingDuration || 30),
          });
        }
        const snap = await getDocs(collection(db, 'companies'));
        if (active) setCompanies(snap.docs
          .filter(d => !['_schema', '_placeholder', companyId].includes(d.id))
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(c => c.companyName || c.name)
          .sort((a, b) => String(a.companyName || a.name || '').localeCompare(String(b.companyName || b.name || ''))));
      } catch (error) {
        console.error('Profile privacy load error:', error);
        toast.error('Unable to load privacy settings.');
      } finally { if (active) setLoading(false); }
    };
    load();
    return () => { active = false; };
  }, [currentUser?.uid, userData, companyId]);

  const blockedCompanyOptions = useMemo(() => companies.filter(c => blockedCompanies.includes(c.id) || blockedCompanies.includes(c.companyName)), [companies, blockedCompanies]);

  const save = async () => {
    if (!currentUser?.uid) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'users', currentUser.uid), {
        privacy: { blockedCountries, blockedCompanies },
        blockedCountries,
        blockedCompanies,
        aiIntroduction: aiIntroDraft.trim(),
        aiIntroductionEditedByUser: true,
        aiIntroductionUpdatedAt: serverTimestamp(),
        workingHours: {
          start: workingHours.start,
          end: workingHours.end,
          days: workingHours.days,
          slotMinutes: Number(workingHours.slotMinutes || 30),
          defaultMeetingDuration: Number(workingHours.defaultMeetingDuration || 30),
        },
        defaultMeetingDuration: Number(workingHours.defaultMeetingDuration || 30),
        availabilityUpdatedAt: serverTimestamp(),
        privacyUpdatedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      toast.success('Privacy settings and AI introduction saved.');
    } catch (error) { toast.error(friendlyError(error, 'Could not save privacy settings.')); }
    finally { setSaving(false); }
  };

  const refreshMatches = async () => {
    if (!companyId) return toast.error('Company context is unavailable.');
    try {
      const result = await regenerateMyMatches(companyId);
      toast.success(`Business matches refreshed${Number.isFinite(result?.newCount) ? ` · ${result.newCount} new` : ''}.`);
    } catch (error) { toast.error(friendlyError(error, 'Could not refresh matches.')); }
  };

  const regenerateIntroduction = async () => {
    if (!companyId) return toast.error('Company context is unavailable.');
    setAiGenerating(true);
    try {
      const result = await generateIntroduction({
        companyId,
        context: { source: 'profile_settings', use: 'saved_default_introduction' },
        tone: 'professional',
      });
      if (!result?.text) throw new Error('AI returned an empty introduction.');
      setAiIntroDraft(result.text);
      toast.success('ChatGPT generated a new editable introduction. Save when you are happy with it.');
    } catch (error) {
      toast.error(friendlyError(error, 'Could not generate an introduction. Check ChatGPT/OpenAI settings.'));
    } finally {
      setAiGenerating(false);
    }
  };

  const uploadProfilePhoto = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!currentUser?.uid) return toast.error('Please sign in before uploading a profile photo.');
    setUploadingPhoto(true);
    try {
      const asset = await uploadFile({ companyId, userId: currentUser.uid, scope: 'user', file, kind: 'image', folder: 'profile-photos' });
      await updateDoc(doc(db, 'users', currentUser.uid), {
        profileImage: asset.url,
        photoURL: asset.url,
        profileImageAsset: asset,
        updatedAt: serverTimestamp(),
      });
      toast.success('Profile photo saved successfully.');
    } catch (error) {
      toast.error(friendlyError(error, 'Could not upload profile photo.'));
    } finally {
      setUploadingPhoto(false);
    }
  };

  const enablePushNotifications = async () => {
    if (!currentUser?.uid) return;
    try {
      const result = await registerPushToken({ uid: currentUser.uid, requestPermission: true });
      if (result?.permission === 'granted') toast.success('Push notifications enabled on this device.');
      else if (result?.configured === false) toast.error('Push notifications are not configured by platform administration yet.');
      else toast('Browser notification permission was not granted.', { icon: '🔕' });
    } catch (error) { toast.error(friendlyError(error, 'Could not enable push notifications.')); }
  };

  const addCountry = () => {
    const value = countryInput.trim();
    if (!value) return;
    if (!blockedCountries.some(x => x.toLowerCase() === value.toLowerCase())) setBlockedCountries(prev => [...prev, value]);
    setCountryInput('');
  };

  const toggleWorkingDay = (day) => {
    setWorkingHours((prev) => ({
      ...prev,
      days: prev.days.includes(day) ? prev.days.filter((item) => item !== day) : [...prev.days, day],
    }));
  };

  const dayOptions = [
    ['monday', 'Mon'], ['tuesday', 'Tue'], ['wednesday', 'Wed'], ['thursday', 'Thu'],
    ['friday', 'Fri'], ['saturday', 'Sat'], ['sunday', 'Sun'],
  ];

  return (
    <Box className="erp-page-shell erp-internal-page" sx={{ minHeight: '100vh', bgcolor: palette.canvas }}>
      <Header />
      <Container maxWidth="xl" sx={{ py: { xs: 2.5, md: 3.5 } }}>
        <Paper elevation={0} className="tf-command-hero tf-profile-settings-hero" sx={{ mb: 2.2, p: { xs: 2, md: 2.3 } }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between' }}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <Box sx={{ width: 48, height: 48, borderRadius: '13px', display: 'grid', placeItems: 'center', color: 'var(--primary-contrast)', background: 'linear-gradient(135deg,var(--primary),var(--secondary))', boxShadow: '0 10px 24px rgba(var(--primary-rgb),.22)' }}><SecurityRounded /></Box>
              <Box>
                <Typography sx={{ fontSize: { xs: '1.25rem', md: '1.5rem' }, fontWeight: 850, color: 'inherit', letterSpacing: '-.025em' }}>Privacy & AI Profile</Typography>
                <Typography sx={{ mt: .35, fontSize: '.76rem', color: 'inherit', opacity: .78 }}>Control who may contact you and review the professional introduction used by TELCOFAIR AI.</Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: 'wrap' }}>
              <Button onClick={enablePushNotifications} startIcon={<NotificationsActiveRounded />} variant="outlined" className="tf-command-ghost-btn">Enable Push</Button>
              <Button onClick={refreshMatches} startIcon={<RefreshRounded />} variant="outlined" className="tf-command-ghost-btn">Refresh Matches</Button>
              <Button onClick={save} disabled={saving} startIcon={saving ? <CircularProgress size={15} color="inherit" /> : <SaveRounded />} variant="contained" sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 800, background: 'linear-gradient(135deg,var(--primary),var(--secondary))', color: 'var(--brand-action-text)' }}>{saving ? 'Saving…' : 'Save'}</Button>
            </Stack>
          </Stack>
        </Paper>

        {loading ? <Box sx={{ minHeight: 300, display: 'grid', placeItems: 'center' }}><CircularProgress /></Box> : <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 7 }}>
            <Stack spacing={2}>
              <Paper elevation={0} sx={cardSx('#7C3AED')}>
                <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', mb: 1.8 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: '12px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(124,58,237,.09)', color: '#6D28D9' }}><PublicRounded /></Box>
                  <Box><Typography sx={{ fontWeight: 850, color: '#172033' }}>Country Restrictions</Typography><Typography sx={{ fontSize: '.7rem', color: '#718096' }}>Users in these countries remain visible, but cannot send introductions, start chat or access your calendar.</Typography></Box>
                </Stack>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                  <TextField fullWidth size="small" label="Add country" value={countryInput} onChange={e => setCountryInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addCountry(); } }} />
                  <Button onClick={addCountry} variant="outlined" sx={{ minWidth: 110, borderRadius: '10px', textTransform: 'none', fontWeight: 750 }}>Add</Button>
                </Stack>
                <Stack direction="row" spacing={.8} useFlexGap sx={{ flexWrap: 'wrap', mt: 1.5 }}>
                  {blockedCountries.map(country => <Chip key={country} label={country} onDelete={() => setBlockedCountries(prev => prev.filter(x => x !== country))} size="small" sx={{ fontWeight: 700 }} />)}
                  {!blockedCountries.length && <Typography sx={{ fontSize: '.72rem', color: '#94A3B8' }}>No country restrictions.</Typography>}
                </Stack>
              </Paper>

              <Paper elevation={0} sx={cardSx('#0F766E')}>
                <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', mb: 1.8 }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: '12px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(15,118,110,.09)', color: '#0F766E' }}><BusinessRounded /></Box>
                  <Box><Typography sx={{ fontWeight: 850, color: '#172033' }}>Blocked Companies</Typography><Typography sx={{ fontSize: '.7rem', color: '#718096' }}>Blocking a company disables communication from every registered user associated with it.</Typography></Box>
                </Stack>
                <Autocomplete
                  multiple options={companies} value={blockedCompanyOptions}
                  getOptionLabel={option => option.companyName || option.name || option.id}
                  isOptionEqualToValue={(a,b) => a.id === b.id}
                  onChange={(_, values) => setBlockedCompanies(values.map(v => v.id))}
                  renderInput={params => <TextField {...params} size="small" label="Select companies to block" placeholder="Search company…" />}
                />
              </Paper>
            </Stack>
          </Grid>

          <Grid size={{ xs: 12, lg: 5 }}>
            <Paper elevation={0} sx={cardSx('#C026D3')}>
              <Stack direction="row" spacing={1.25} sx={{ alignItems: 'center', mb: 1.5 }}>
                <Box sx={{ position: 'relative' }}>
                  <Avatar src={userData?.profileImage || userData?.photoURL || currentUser?.photoURL || undefined} sx={{ width: 50, height: 50, boxShadow: '0 8px 20px rgba(var(--primary-rgb),.16)' }} />
                  <Button component="label" disabled={uploadingPhoto} size="small" sx={{ position: 'absolute', minWidth: 0, width: 25, height: 25, p: 0, right: -7, bottom: -5, borderRadius: '8px', bgcolor: 'var(--primary)', color: 'var(--primary-contrast)', boxShadow: '0 5px 12px rgba(var(--primary-rgb),.22)', '&:hover': { bgcolor: 'var(--primary)' } }}>
                    {uploadingPhoto ? <CircularProgress size={12} color="inherit" /> : <PhotoCameraRounded sx={{ fontSize: 14 }} />}
                    <input hidden type="file" accept="image/*" onChange={uploadProfilePhoto} />
                  </Button>
                </Box>
                <Box><Typography sx={{ fontWeight: 850, color: '#172033' }}>{userData?.displayName || [userData?.firstName,userData?.lastName].filter(Boolean).join(' ') || currentUser?.email}</Typography><Typography sx={{ fontSize: '.7rem', color: '#718096' }}>{userData?.businessTitle || userData?.title || userData?.designation || 'TELCOFAIR member'} · {userData?.companyName || companyData?.companyName || ''}</Typography><Typography sx={{ mt: .25, fontSize: '.62rem', color: '#94A3B8' }}>Photo uses the company-selected storage provider.</Typography></Box>
              </Stack>
              <Alert severity="info" icon={<AutoAwesomeRounded />} sx={{ mb: 1.5, borderRadius: '12px' }}>When ChatGPT AI is enabled in Company Settings, your default introduction is regenerated automatically when your business profile changes.</Alert>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between', mb: .8 }}>
                <Typography sx={{ fontSize: '.68rem', fontWeight: 800, color: '#64748B', textTransform: 'uppercase', letterSpacing: '.06em' }}>Saved AI Introduction</Typography>
                <Button
                  size="small"
                  onClick={regenerateIntroduction}
                  disabled={aiGenerating || !companyId}
                  startIcon={aiGenerating ? <CircularProgress size={13} color="inherit" /> : <AutoAwesomeRounded />}
                  sx={{ borderRadius: '9px', textTransform: 'none', fontWeight: 780, color: 'var(--accent-ink)', bgcolor: 'rgba(var(--accent-rgb),.065)', border: '1px solid rgba(var(--accent-rgb),.11)', '&:hover': { bgcolor: 'rgba(var(--accent-rgb),.11)' } }}
                >
                  {aiGenerating ? 'Generating…' : 'Regenerate with ChatGPT'}
                </Button>
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={7}
                value={aiIntroDraft}
                onChange={(e) => setAiIntroDraft(e.target.value)}
                placeholder="Your editable TELCOFAIR business introduction will appear here."
                helperText="This is the one-click introduction used by the directory. You can edit the ChatGPT draft before saving or sending it."
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '12px', bgcolor: 'rgba(var(--primary-rgb),.025)', alignItems: 'flex-start' }, '& textarea': { fontSize: '.8rem', lineHeight: 1.7, color: '#334155' }, '& .MuiFormHelperText-root': { fontSize: '.66rem' } }}
              />
              <Stack direction="row" spacing={.8} useFlexGap sx={{ flexWrap: 'wrap', mt: 1.5 }}>
                <Chip icon={<AutoAwesomeRounded />} label="AI assisted" size="small" sx={{ fontWeight: 750 }} />
                <Chip icon={<BlockRounded />} label={`${blockedCountries.length} countries blocked`} size="small" sx={{ fontWeight: 750 }} />
                <Chip icon={<BusinessRounded />} label={`${blockedCompanies.length} companies blocked`} size="small" sx={{ fontWeight: 750 }} />
              </Stack>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Paper elevation={0} sx={cardSx('#EA580C')}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.4} sx={{ alignItems: { md: 'center' }, justifyContent: 'space-between', mb: 1.8 }}>
                <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center' }}>
                  <Box sx={{ width: 42, height: 42, borderRadius: '12px', display: 'grid', placeItems: 'center', bgcolor: 'rgba(234,88,12,.09)', color: '#C2410C', boxShadow: '0 8px 18px rgba(234,88,12,.12)' }}><AccessTimeRounded /></Box>
                  <Box>
                    <Typography sx={{ fontWeight: 850, color: '#172033' }}>Shared Meeting Availability</Typography>
                    <Typography sx={{ fontSize: '.7rem', color: '#718096' }}>Define the working window you are willing to share. It becomes bookable only after you choose <b>Share My Calendar</b> for an introduction.</Typography>
                  </Box>
                </Stack>
                <Chip icon={<EventAvailableRounded />} size="small" label="Recipient controlled" sx={{ fontWeight: 750, bgcolor: 'rgba(234,88,12,.07)', color: '#9A3412' }} />
              </Stack>

              <Grid container spacing={1.35}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField fullWidth size="small" type="time" label="Available from" value={workingHours.start} onChange={(e) => setWorkingHours((prev) => ({ ...prev, start: e.target.value }))} slotProps={{ inputLabel: { shrink: true } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField fullWidth size="small" type="time" label="Available until" value={workingHours.end} onChange={(e) => setWorkingHours((prev) => ({ ...prev, end: e.target.value }))} slotProps={{ inputLabel: { shrink: true } }} />
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField fullWidth select size="small" label="Slot interval" value={workingHours.slotMinutes} onChange={(e) => setWorkingHours((prev) => ({ ...prev, slotMinutes: Number(e.target.value) }))}>
                    {[15, 30, 45, 60].map((minutes) => <MenuItem key={minutes} value={minutes}>{minutes} minutes</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <TextField fullWidth select size="small" label="Default meeting duration" value={workingHours.defaultMeetingDuration} onChange={(e) => setWorkingHours((prev) => ({ ...prev, defaultMeetingDuration: Number(e.target.value) }))}>
                    {[15, 30, 45, 60].map((minutes) => <MenuItem key={minutes} value={minutes}>{minutes} minutes</MenuItem>)}
                  </TextField>
                </Grid>
                <Grid size={{ xs: 12 }}>
                  <Typography sx={{ fontSize: '.68rem', color: '#64748B', fontWeight: 800, mb: .85, textTransform: 'uppercase', letterSpacing: '.05em' }}>Available days</Typography>
                  <Stack direction="row" spacing={.75} useFlexGap sx={{ flexWrap: 'wrap' }}>
                    {dayOptions.map(([value, label]) => {
                      const activeDay = workingHours.days.includes(value);
                      return <Chip key={value} label={label} clickable onClick={() => toggleWorkingDay(value)} sx={{ fontWeight: 800, borderRadius: '9px', bgcolor: activeDay ? 'rgba(var(--primary-rgb),.10)' : '#F8FAFC', color: activeDay ? 'var(--primary-ink)' : '#64748B', border: `1px solid ${activeDay ? 'rgba(var(--primary-rgb),.20)' : 'rgba(25,57,78,.08)'}` }} />;
                    })}
                  </Stack>
                </Grid>
              </Grid>
              <Alert severity="info" sx={{ mt: 1.6, borderRadius: '12px' }}>Connected busy-time sources are used only to remove conflicting time from these windows. The other person sees free slots—not private calendar details.</Alert>
            </Paper>
          </Grid>

        </Grid>}
      </Container>
    </Box>
  );
}
